import React, { useContext, useEffect, useState } from 'react';
import { ReactReduxContext } from 'react-redux';
import { copyText, shareLink } from '../../../shared/utils/telegramLinks';
import { buildInviteLink } from './pvpInvite';
import { notify } from './haptics';
import './inviteRoom.css';
import { Trans, useTranslation } from 'react-i18next';

/**
 * «Play with a friend» UI shared by the Arena and Durak:
 *  - InviteWaiting — the host's screen while the room is open (code, link, Copy / Share, expiry countdown);
 *  - InviteBanner  — the friend's card in the lobby (who invites, stake, Accept).
 */

function formatStake(t, stake) {
  const n = Number(stake) || 0;
  if (n === 0) return t('invite_room.free_game', 'Free game');
  return `${String(Number(n.toFixed(4)))} GRAM`;
}

function useSecondsLeft(expiresInSec, since) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (expiresInSec == null) return null;
  return Math.max(0, Math.round(Number(expiresInSec) - (now - since) / 1000));
}

export function InviteWaiting({
  game, room, connection, onCancel,
}) {
  const { t } = useTranslation();
  // the store is optional (pages are tested without a Provider)
  const redux = useContext(ReactReduxContext);
  const telegramId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id ?? redux?.store?.getState()?.user?.data?.telegramId;
  const [copied, setCopied] = useState(false);
  const [since, setSince] = useState(Date.now());
  useEffect(() => { setSince(Date.now()); }, [room?.code, room?.expiresInSec]);
  const left = useSecondsLeft(room?.expiresInSec, since);

  const link = room?.code ? buildInviteLink(game, room.code, telegramId) : '';
  const shareText = game === 'durak'
    ? t('invite_room.share_durak', '🃏 Join my Durak table in Datum Empire!')
    : t('invite_room.share_arena', '⚔ Join my Arena duel in Datum Empire!');

  const onCopy = async () => {
    if (!link) return;
    const ok = await copyText(link);
    if (ok) {
      notify('success');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const onShare = async () => {
    if (!link) return;
    const how = await shareLink(link, shareText);
    if (how === 'copied') {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const connecting = connection && connection !== 'open';

  return (
    <div className="inv-wait">
      <div className="inv-wait__pulse" aria-hidden="true">
        <span />
        <span />
        <span className="inv-wait__icon">👥</span>
      </div>
      <div className="inv-wait__title">
        {!room?.code
          ? t('invite_room.creating', 'Creating an invite…')
          : connecting ? t('pvp.reconnecting', 'Reconnecting…') : t('invite_room.waiting', 'Waiting for your friend…')}
      </div>
      <div className="inv-wait__stake">{formatStake(t, room?.stake)}</div>

      {room?.code && (
        <>
          <div className="inv-code" aria-label={t('invite_room.code_aria', 'Invite code')}>
            {room.code.split('').map((ch, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={i} className="inv-code__ch">{ch}</span>
            ))}
          </div>
          <div className="inv-link" title={link}>{link}</div>
          <div className="inv-actions">
            <button type="button" className="app-btn app-btn--green" onClick={onCopy}>
              {copied ? `✓ ${t('invite_room.link_copied', 'Link copied')}` : `🔗 ${t('invite_room.copy_link', 'Copy link')}`}
            </button>
            <button type="button" className="app-btn app-btn--blue" onClick={onShare}>📨 {t('invite_room.send', 'Send to a friend')}</button>
          </div>
          <div className="inv-wait__hint">
            {t('invite_room.keep_open', 'Keep this screen open — the game starts as soon as your friend taps the link.')}
            {left != null && left > 0 && (
              <> {t('invite_room.expires_in', 'Invite expires in {{time}}.', {
                time: `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`,
              })}</>
            )}
          </div>
        </>
      )}

      <button type="button" className="app-btn app-btn--purple inv-wait__cancel" onClick={onCancel}>{t('invite_room.cancel', 'Cancel invite')}</button>
    </div>
  );
}

export function InviteBanner({
  invite, loading, error, onAccept, onDismiss, acceptDisabled, acceptHint,
}) {
  const { t } = useTranslation();
  if (!invite && !loading && !error) return null;
  return (
    <div className={`inv-banner${error ? ' inv-banner--error' : ''}`} role="status">
      <button type="button" className="inv-banner__close" onClick={onDismiss} aria-label={t('common.dismiss', 'Dismiss')}>×</button>
      {loading && <div className="inv-banner__text">{t('invite_room.loading', 'Loading the invite…')}</div>}
      {!loading && error && <div className="inv-banner__text">{error}</div>}
      {!loading && !error && invite && (
        <>
          <div className="inv-banner__title">
            <span aria-hidden="true">👥</span> {invite.own ? t('invite_room.own', 'Your own invite') : (
              <Trans
                i18nKey="invite_room.invites_you"
                defaults="<b>{{name}}</b> invites you to play"
                values={{ name: invite.hostName || t('invite_room.a_friend', 'A friend') }}
                components={{ b: <b /> }}
              />
            )}
          </div>
          <div className="inv-banner__meta">
            <span className="inv-banner__stake">{formatStake(t, invite.stake)}</span>
            {!invite.own && invite.hostOnline === false && <span className="inv-banner__off">{t('invite_room.friend_offline', 'friend is offline')}</span>}
          </div>
          {invite.own ? (
            <div className="inv-banner__text">{t('invite_room.own_hint', "Send this link to a friend — you can't join your own room.")}</div>
          ) : (
            <>
              <button type="button" className="app-btn app-btn--green inv-banner__accept" disabled={acceptDisabled} onClick={onAccept}>
                {t('invite_room.accept', 'Accept & play')}
              </button>
              {acceptHint && <div className="inv-banner__hint">{acceptHint}</div>}
            </>
          )}
        </>
      )}
    </div>
  );
}
